import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { performStatusTransition } from '../actions/performStatusTransition';
import { JustifiedAction } from '../actions/JustifiedAction';
import { CrossRegionConfirm } from '../actions/CrossRegionConfirm';
import type { AccessibleRegion } from '../auth/permissions';
import type {
  AdminDataProvider,
  AffiliateNetwork,
  AffiliateNetworkMarket,
  Market,
} from '../dataProvider';

/**
 * Affiliate network editor (D-settings-s2-networks, Phase 9 S2). PROMOTES the
 * read-only affiliateNetworks reference data to editable CRUD (edit-only, no create
 * / delete). One form with sections, the per-field trust applied within it:
 *
 * - Details (plain audited): name / clickParam / deeplinkParam / trackingDomain.
 *   slug / storeCount / lastImportedAt are shown read-only for context (slug is the
 *   immutable key, the other two are derived / import-set).
 * - Commission (FORCED REASON): the per-market defaultCommissionRate rows. A
 *   single-market change reuses JustifiedAction; a both-market change routes through
 *   the S1 CrossRegionConfirm plus two per-market audited writes.
 * - Credentials (REDACTED): a write-only masked input. The secret is never read back
 *   into the form, and a change records [redacted] in the audit (SENSITIVE_AUDIT_FIELDS).
 *
 * The whole surface gates networks:write (super_admin-only; the editor holds no
 * networks grant). The import job reading the credential is the migration seam.
 */

const marketToRegion = (m: Market): AccessibleRegion =>
  m === 'us' ? 'US' : 'UK';

const NetworkEditInner = ({
  record,
  markets,
}: {
  record: AffiliateNetwork;
  markets: AffiliateNetworkMarket[];
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();

  const [name, setName] = useState(record.name);
  const [clickParam, setClickParam] = useState(record.clickParam);
  const [deeplinkParam, setDeeplinkParam] = useState(record.deeplinkParam);
  const [trackingDomain, setTrackingDomain] = useState(record.trackingDomain);

  const [newCredential, setNewCredential] = useState('');

  const [rates, setRates] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      markets.map((m) => [m.id, String(m.defaultCommissionRate)]),
    ),
  );

  const [bothRate, setBothRate] = useState('');
  const [bothReason, setBothReason] = useState('');
  const [pendingCross, setPendingCross] = useState(false);
  const [busy, setBusy] = useState(false);

  const canApplyBoth = markets.length > 1;

  // The details write: a plain audited update, NO forced reason (the low-stakes
  // param edits, the S1 site-name posture). apiCredential is NOT in the patch, so
  // it is untouched.
  const saveDetails = async () => {
    if (!name.trim()) {
      notify('A network name is required', { type: 'error' });
      return;
    }
    try {
      await dataProvider.update('affiliateNetworks', {
        id: record.id,
        data: { name, clickParam, deeplinkParam, trackingDomain },
        previousData: record,
      });
      notify('Network details saved', { type: 'success' });
      refresh();
    } catch {
      notify('Could not save the details', { type: 'error' });
    }
  };

  // The credential write: write-only. Only writes when a NEW value is entered, so a
  // blank input never clears the credential. Redacted in the audit (the field is in
  // SENSITIVE_AUDIT_FIELDS, so the change records [redacted] not the secret).
  const saveCredential = async () => {
    if (!newCredential.trim()) {
      notify('Enter a new credential to change it', { type: 'warning' });
      return;
    }
    try {
      await dataProvider.update('affiliateNetworks', {
        id: record.id,
        data: { apiCredential: newCredential.trim() },
        previousData: record,
      });
      notify('Credential updated', { type: 'success' });
      setNewCredential('');
      refresh();
    } catch {
      notify('Could not update the credential', { type: 'error' });
    }
  };

  // Both-market commission: the reason is forced (the Save button is disabled until
  // it is entered), the regions are named (CrossRegionConfirm), and each market row
  // is a separate audited write through the chokepoint (the reason rides in the patch).
  const saveBothCommission = async () => {
    setBusy(true);
    try {
      for (const mkt of markets) {
        await performStatusTransition({
          dataProvider,
          role,
          capability: 'networks:write',
          resource: 'affiliateNetworkMarkets',
          id: mkt.id,
          data: {
            defaultCommissionRate: Number(bothRate),
            commissionChangeReason: bothReason.trim(),
          },
          previousData: mkt,
        });
      }
      notify('Commission saved to both markets', { type: 'success' });
      setPendingCross(false);
      refresh();
    } catch {
      notify('Could not save the commission', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const credentialSet =
    record.apiCredential != null && record.apiCredential !== '';

  return (
    <Box sx={{ maxWidth: 820 }} data-testid="network-edit">
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit network: {record.name}
      </Typography>

      {/* Details (plain audited) + the read-only context fields. */}
      <Card sx={{ mb: 2 }} data-testid="network-details">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Details
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'network-name' }}
            />
            <TextField
              label="Tracking domain"
              value={trackingDomain}
              onChange={(e) => setTrackingDomain(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'network-domain' }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Click param"
              value={clickParam}
              onChange={(e) => setClickParam(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'network-clickparam' }}
            />
            <TextField
              label="Deeplink param"
              value={deeplinkParam}
              onChange={(e) => setDeeplinkParam(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'network-deeplinkparam' }}
            />
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          {/* Read-only context: slug (the immutable key), storeCount (derived),
              lastImportedAt (import-set). Display only, no inputs. */}
          <Stack
            direction="row"
            spacing={3}
            useFlexGap
            flexWrap="wrap"
            data-testid="network-readonly"
          >
            <Typography variant="body2" color="text.secondary">
              Slug (fixed): {record.slug}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stores on this network: {String(record.storeCount ?? 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last imported:{' '}
              {new Date(record.lastImportedAt).toLocaleDateString()}
            </Typography>
          </Stack>
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              onClick={saveDetails}
              data-testid="network-save-details"
            >
              Save details
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Commission (forced reason; both-market via CrossRegionConfirm). */}
      <Card sx={{ mb: 2 }} data-testid="network-commission">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Per-market commission defaults
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The default commission affects earnings, so a change records a
            reason.
          </Typography>
          <Stack spacing={2}>
            {markets.map((mkt) => (
              <Stack
                key={mkt.id}
                direction="row"
                spacing={2}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <TextField
                  label={`${mkt.market.toUpperCase()} default (%)`}
                  value={rates[mkt.id] ?? ''}
                  onChange={(e) =>
                    setRates((r) => ({ ...r, [mkt.id]: e.target.value }))
                  }
                  type="number"
                  margin="dense"
                  size="small"
                  sx={{ minWidth: 200 }}
                  inputProps={{ 'data-testid': `net-commission-${mkt.market}` }}
                />
                <JustifiedAction
                  resource="affiliateNetworkMarkets"
                  capability="networks:write"
                  id={mkt.id}
                  label={`Save ${mkt.market.toUpperCase()} commission`}
                  dialogTitle={`Change the ${mkt.market.toUpperCase()} commission default`}
                  dialogIntro="This changes the default commission for the market. Record why."
                  buildData={(reason) => ({
                    defaultCommissionRate: Number(rates[mkt.id]),
                    commissionChangeReason: reason,
                  })}
                  previousData={mkt}
                  successMessage="Commission saved"
                />
              </Stack>
            ))}
          </Stack>

          {canApplyBoth && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Apply the same rate to both markets
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <TextField
                  label="Rate for both (%)"
                  value={bothRate}
                  onChange={(e) => setBothRate(e.target.value)}
                  type="number"
                  margin="dense"
                  size="small"
                  sx={{ minWidth: 160 }}
                  inputProps={{ 'data-testid': 'net-commission-both' }}
                />
                <TextField
                  label="Reason"
                  value={bothReason}
                  onChange={(e) => setBothReason(e.target.value)}
                  margin="dense"
                  size="small"
                  sx={{ minWidth: 220 }}
                  inputProps={{ 'data-testid': 'net-both-reason' }}
                />
                <Button
                  variant="contained"
                  disabled={!bothReason.trim() || !bothRate.trim() || busy}
                  onClick={() => setPendingCross(true)}
                  data-testid="net-save-both"
                >
                  Save to both markets
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* Credentials (redacted; write-only). */}
      <Card sx={{ mb: 2 }} data-testid="network-credential">
        <CardContent>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography variant="subtitle2">Import credential</Typography>
            <Chip
              size="small"
              label={credentialSet ? 'Credential is set' : 'No credential set'}
              color={credentialSet ? 'success' : 'default'}
              variant={credentialSet ? 'filled' : 'outlined'}
              data-testid="network-credential-tell"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
            The account secret the import job authenticates with. It is never
            shown; enter a new value to change it.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Enter a new value to change"
              value={newCredential}
              onChange={(e) => setNewCredential(e.target.value)}
              type="password"
              margin="dense"
              size="small"
              sx={{ minWidth: 260 }}
              inputProps={{ 'data-testid': 'network-credential-input' }}
            />
            <Button
              variant="contained"
              onClick={saveCredential}
              data-testid="network-save-credential"
            >
              Save credential
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <CrossRegionConfirm
        isOpen={pendingCross}
        loading={busy}
        regions={markets.map((m) => marketToRegion(m.market))}
        what="commission default"
        onConfirm={saveBothCommission}
        onClose={() => setPendingCross(false)}
      />
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const NetworkEditView = () => {
  const { id } = useParams();
  const netId = Number(id);
  const { data: record, isLoading } = useGetOne<AffiliateNetwork>(
    'affiliateNetworks',
    { id: netId },
  );
  const { data: markets, isLoading: marketsLoading } =
    useGetList<AffiliateNetworkMarket>('affiliateNetworkMarkets', {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'market', order: 'ASC' },
      filter: { networkId: netId },
    });
  if (isLoading || marketsLoading || !record) return <Loading />;
  return <NetworkEditInner record={record} markets={markets ?? []} />;
};

/** Gated entry point registered as the affiliateNetworks Resource's edit. */
export const NetworkEdit = () => (
  <RequireAccess capability="networks:write">
    <NetworkEditView />
  </RequireAccess>
);
